import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import QRCode from 'qrcode';
import { ArrowRight, Copy, Gift, HandHeart, HeartHandshake, QrCode, ShieldCheck, Sparkles } from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../config/featureFlags';
import '../../styles/components/MinistrySupportPage.css';

const presetAmounts = [25, 50, 100, 250];

export default function MinistrySupportPage() {
  const { groupId } = useParams();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const apiUrl = getApiBaseUrl();
  const stripeEnabled = isFeatureEnabled(FEATURE_FLAGS.groupStripeFinance);
  const [support, setSupport] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [amount, setAmount] = useState('50');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const giftKey = useRef(crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/ministries/${groupId}/support?source=shared`;
  }, [groupId]);

  useEffect(() => {
    document.title = 'Ministry Support | ThousandYearKingdom.org';
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/api/ministry-support/${groupId}${window.location.search || ''}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Could not load this ministry support page.');
        return response.json();
      })
      .then((data) => { if (!cancelled) setSupport(data); })
      .catch((error) => { if (!cancelled) setMessage(error.message); });
    return () => { cancelled = true; };
  }, [apiUrl, groupId]);

  useEffect(() => {
    if (!shareUrl) return;
    QRCode.toDataURL(shareUrl, { margin: 1, width: 260, color: { dark: '#07111f', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [shareUrl]);

  const authenticatedRequest = async (path, method = 'POST', body) => {
    const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE } });
    const response = await fetch(`${apiUrl}/api/${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.detail || 'That support action is unavailable right now.');
    return data;
  };

  const donate = async (event) => {
    event.preventDefault();
    setMessage('');
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `/ministries/${groupId}/support` } });
      return;
    }
    setBusy(true);
    try {
      const result = await authenticatedRequest(`group-finance/${groupId}/checkout`, 'POST', {
        requestKey: giftKey.current,
        amount: Number(amount),
        description: `${support?.ministry?.groupName || 'Ministry'} support gift`,
        anonymous: false,
      });
      if (result.checkoutUrl) window.location.assign(result.checkoutUrl);
      else setMessage(`Payment status: ${result.status}`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setMessage('Support link copied.');
    } catch {
      setMessage(shareUrl);
    }
  };

  if (!support && !message) {
    return <main className="ministry-support-page"><p className="ministry-support-loading">Loading ministry support page...</p></main>;
  }

  const ministry = support?.ministry || {};
  const giving = support?.giving || {};
  const location = [ministry.city, ministry.state, ministry.country].filter(Boolean).join(', ');
  const canGiveOnline = stripeEnabled && giving.canReceiveStripe;

  return (
    <main className="ministry-support-page">
      <section className="ministry-support-hero">
        <div className="ministry-support-hero-copy">
          <span className="ministry-support-kicker"><Sparkles size={16} /> Kingdom Movement support</span>
          <h1>{ministry.groupName || 'Ministry support'}</h1>
          <p className="ministry-support-lead">
            {ministry.groupMission || 'Help this ministry gather prayer, provision, volunteers, and follow-up so more people encounter Christ and the Body of Christ can move together.'}
          </p>
          <div className="ministry-support-actions">
            <a className="primary" href="#support-giving">Support this mission <ArrowRight size={18} /></a>
            <Link to={`/needs?groupId=${groupId}`}>See needs</Link>
            <Link to={`/group/${groupId}`}>Ministry profile</Link>
          </div>
        </div>
        <aside className="ministry-support-qr-card">
          <span><QrCode size={18} /> Event QR link</span>
          {qrDataUrl && <img src={qrDataUrl} alt="QR code for this ministry support page" />}
          <button type="button" onClick={copyShareLink}><Copy size={16} /> Copy share link</button>
          <small>Print this QR for a welcome table, flyer, speaker slide, or event booth.</small>
        </aside>
      </section>

      {message && <p className="ministry-support-notice" role="status">{message}</p>}

      <section className="ministry-support-grid">
        <article className="ministry-support-panel" id="support-giving">
          <span className="ministry-support-kicker"><Gift size={16} /> Give</span>
          <h2>Help carry the work.</h2>
          <p>
            Gifts should go to the ministry's own receiving account when Stripe is connected.
            Cash or check support can be recorded by trusted treasurers so the group can keep a transparent receipt record.
          </p>
          <form onSubmit={donate} className="ministry-support-giving-form">
            <div className="ministry-support-amounts">
              {presetAmounts.map((value) => (
                <button type="button" key={value} className={String(value) === String(amount) ? 'selected' : ''} onClick={() => { setAmount(String(value)); giftKey.current = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`; }}>
                  ${value}
                </button>
              ))}
            </div>
            <label>
              Amount
              <input type="number" min="0.5" max="999999" step="0.01" value={amount} onChange={(event) => { setAmount(event.target.value); giftKey.current = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`; }} />
            </label>
            <button type="submit" disabled={busy || !canGiveOnline}>
              {canGiveOnline ? 'Give through Stripe' : 'Online giving not connected yet'}
            </button>
          </form>
          {!canGiveOnline && (
            <div className="ministry-support-warning">
              <ShieldCheck size={18} />
              <p>
                Stripe is not ready for this ministry yet. A treasurer or ministry representative should connect the receiving account from the finance page before accepting card gifts.
              </p>
            </div>
          )}
          <Link className="ministry-support-text-link" to={`/groups/${groupId}/finance`}>Open giving and stewardship records</Link>
        </article>

        <article className="ministry-support-panel">
          <span className="ministry-support-kicker"><HandHeart size={16} /> Needs</span>
          <h2>Answer something real.</h2>
          {support?.openNeeds?.length ? (
            <div className="ministry-support-needs-list">
              {support.openNeeds.map((need) => (
                <Link key={need.id} to={`/needs?groupId=${groupId}&need=${need.id}`}>
                  <strong>{need.title}</strong>
                  <span>{need.category} · {need.responseCount || 0} response{Number(need.responseCount || 0) === 1 ? '' : 's'}</span>
                </Link>
              ))}
            </div>
          ) : (
            <p>No open ministry needs have been posted yet.</p>
          )}
          <Link className="ministry-support-text-link" to={`/needs?groupId=${groupId}&post=1`}>Post or answer ministry needs</Link>
        </article>

        <article className="ministry-support-panel">
          <span className="ministry-support-kicker"><HeartHandshake size={16} /> Stewardship</span>
          <h2>Transparent support, not mystery money.</h2>
          <p>
            Confirmed support: <strong>${Number(giving.totalConfirmed || 0).toFixed(2)}</strong> across <strong>{giving.confirmedGiftCount || 0}</strong> recorded gift{Number(giving.confirmedGiftCount || 0) === 1 ? '' : 's'}.
          </p>
          <p>
            Manual giving such as cash, checks, Western Union, or direct handoff should be received by trusted people and recorded with receipts when possible.
          </p>
          <p className="ministry-support-small">
            {giving.stripeConnected ? 'Stripe is connected; refresh account readiness from finance before large event traffic.' : 'Stripe has not been connected for this ministry yet.'}
          </p>
        </article>

        <article className="ministry-support-panel">
          <span className="ministry-support-kicker">Ministry details</span>
          <h2>{location || 'Location not listed'}</h2>
          <dl className="ministry-support-details">
            <div><dt>Focus</dt><dd>{ministry.groupCategory || 'Faith'}</dd></div>
            <div><dt>What they offer</dt><dd>{ministry.ministriesOffered || 'Not listed yet'}</dd></div>
            <div><dt>Current needs</dt><dd>{ministry.needs || 'Not listed yet'}</dd></div>
            <div><dt>Resources</dt><dd>{ministry.resourcesAvailable || 'Not listed yet'}</dd></div>
          </dl>
        </article>
      </section>
    </main>
  );
}
